import { Accordion, Container, Grid } from "basis";
import { useAccounts } from "library/accounts";
import { enhanceAccounts } from "library/accounts/enhanceAccounts";
import { sortAccounts } from "library/accounts/sortAccounts";
import { EnhancedAccount } from "library/accounts/types";
import { PageTracking } from "library/hoc/PageTracking";
import { useProducts } from "library/hooks/useProducts";
import { Fragment, useMemo } from "react";
import { NonEmptyArray, PageData } from "../../types";
import { AccountLinkCard } from "../AccountLink/AccountLinkCard";
import { AccountCard } from "./AccountCard";

export const AccountListComponent = () => {
  const accountsInfo = useAccounts();
  const productsInfo = useProducts();
  const products = productsInfo.data ?? [];
  const { activeAccounts, closedAccounts } = useMemo(() => {
    if (!accountsInfo.data || !productsInfo.data) {
      return {
        activeAccounts: [],
        closedAccounts: [],
      };
    }

    const enhancedAccounts = enhanceAccounts(
      [...accountsInfo.data.credit_cards, ...accountsInfo.data.personal_loans],
      productsInfo.data,
    );

    return sortAccounts(enhancedAccounts).reduce<{
      activeAccounts: EnhancedAccount[];
      closedAccounts: EnhancedAccount[];
    }>(
      (acc, account) => {
        if (account.status === "CLOSED") {
          acc.closedAccounts.push(account);
        } else {
          acc.activeAccounts.push(account);
        }

        return acc;
      },
      {
        activeAccounts: [],
        closedAccounts: [],
      },
    );
  }, [accountsInfo.data, productsInfo.data]);

  return (
    <Fragment>
      <Grid cols={4} cols-md={8} colsGap="30px" rowsGap="8">
        {activeAccounts.map((account, index) => (
          <Grid.Item colSpan="auto / span 4" key={account.account_id}>
            <AccountCard
              account={account}
              product={products.find(({ id }) => id === account.product_id)}
              index={index}
            />
          </Grid.Item>
        ))}
        <Grid.Item colSpan="auto / span 4">
          <AccountLinkCard
            header="Got another Latitude account?"
            body="Do you have another Latitude Credit Card or Personal loan account that you'd like to see in your all in one view here?"
          />
        </Grid.Item>
      </Grid>
      {closedAccounts.length > 0 && (
        <Container margin="10 0 0 0">
          <Accordion>
            <Accordion.Item>
              <Accordion.Item.Header>
                Closed accounts ({closedAccounts.length})
              </Accordion.Item.Header>
              <Accordion.Item.Content>
                <Grid cols={4} cols-md={8} colsGap="30px" rowsGap="8">
                  {closedAccounts.map((account, index) => (
                    <Grid.Item colSpan="auto / span 4" key={account.account_id}>
                      <AccountCard
                        account={account}
                        product={products.find(
                          ({ id }) => id === account.product_id,
                        )}
                        index={index}
                      />
                    </Grid.Item>
                  ))}
                </Grid>
              </Accordion.Item.Content>
            </Accordion.Item>
          </Accordion>
        </Container>
      )}
    </Fragment>
  );
};

const pageTracking: PageData = {
  page: {
    pageType: "category-page",
    siteSection: "accounts",
    siteSubSection: "",
    pageTitle: "overview",
  },
};
interface Props {
  Component?: typeof AccountListComponent;
  productCodes: NonEmptyArray<string>;
}

export const AccountList = ({
  Component = AccountListComponent,
  ...props
}: Props) => (
  <PageTracking pageData={{ ...pageTracking, productId: props.productCodes }}>
    <Component />
  </PageTracking>
);
